import {Event} from '../lib/constants/Event'

/**
 * Registering and triggering events
 *
 * @class
 * @alias api.events
 */
class Events {
    /**
     * @hideconstructor
     */
    constructor() {
        this.eventListeners = []
    }

    /**
     * Remove all registered events
     */
    removeAllEventListeners() {
        this.eventListeners = []
    }

    /**
     * Register event listener for a specific plugin
     *
     * @example
     * import {api, Event} from 'writer'
     * api.events.on('myplugin', Event.DOCUMENT_CHANGED, (event) => {
     *    // React on event
     * })
     *
     * @param {string} name - The plugin which listens for the event.
     * @param {string} eventType - The event to listen for.
     * @param {Function} func - Function to call when event is triggered.
     */
    on(name, eventType, func) {
        if (this.eventListeners.find(entry => {
            return entry.name === name && entry.eventType === eventType
        })) {
            console.warn(`Listener for event ${eventType} from <${name}> already registered, ignoring`)
            return
        }

        this.eventListeners.push({
            name: name,
            eventType: eventType,
            func: func
        })
    }

    /**
     * Unregister event listener for a specific plugin.
     *
     * @example
     * import {api, Event} from 'writer'
     * api.events.off('myplugin', Event.DOCUMENT_CHANGED)
     *
     * @param {string} name The plugin which listens for the event.
     * @param {string} eventType The event to stop listening for.
     */
    off(name, eventType) {
        this.eventListeners = this.eventListeners.filter(entry => {
            return !(entry.name === name && entry.eventType === eventType)
        })
    }

    /**
     * Trigger an event of specified type with optional specified data
     *
     * @example
     * import {api, Event} from 'writer'
     * api.events.trigger(
     *    'myplugin',
     *    Event.DOCUMENT_CHANGED,
     *    {
     *       type: 'edit',
     *       action: 'edit',
     *       data: {
     *          key: 'strange things'
     *       }
     *    }
     * )
     *
     * @param  {string} name Plugin name or originator of event
     * @param  {string} eventType Event type to trigger (mandatory)
     * @param  {object} [data=null] Optional data structure to send with event
     */
    trigger(name, eventType, data = null) {
        // Sanity check
        if (!eventType) {
            throw new Error('No eventType supplied when triggering event')
        }

        this.eventListeners.forEach(entry => {
            if (entry.name !== name && entry.eventType === eventType) {
                entry.func({
                    name: name,
                    eventType: eventType,
                    data: data
                })
            }
        })
    }


    /**
     * Use `api.events.trigger()` instead
     *
     * @deprecated 2018-11-20
     */
    triggerEvent(name, eventType, data) {
        console.warn(`api.events.triggerEvent() is deprecated, use api.events.trigger()`)
        this.trigger(name, eventType, data)
    }

    /**
     * Use `api.events.trigger()` with `Event.DOCUMENT_CHANGED` instead
     *
     * @deprecated 2018-11-20
     */
    documentChanged(name, data) {
        if (!data.type) {
            console.warn('Document changed event triggered without type', name)
        }

        if (!data.action) {
            console.warn('Document changed event triggered without action', name)
        }
        else {
            if (-1 === ['set', 'add', 'delete', 'update', 'edit'].indexOf(data.action)) {
                console.warn('Document change event has unknown action', data.action, name)
            }
        }

        console.warn(`api.events.documentChanged() is deprecated, use api.events.trigger()`)
        this.trigger(name, Event.DOCUMENT_CHANGED, data)
    }

    /**
     * Use `api.events.trigger()` with `Event.DOCUMENT_CHANGED_EXTERNAL` instead
     *
     * @deprecated 2018-11-20
     */
    externalUpdate(data) {
        console.warn(`api.events.externalUpdate() is deprecated, use api.events.trigger()`)
        this.trigger(null, Event.DOCUMENT_CHANGED_EXTERNAL, data)
    }

    /**
     * Use `api.events.trigger()` with `Event.DOCUMENT_REPLACED` instead
     *
     * @deprecated 2018-11-20
     */
    documentReplaced() {
        console.warn(`api.events.documentReplaced() is deprecated, use api.events.trigger()`)
        this.trigger(null, Event.DOCUMENT_REPLACED)
    }

    /**
     * Use `api.events.trigger()` with `Event.DOCUMENT_SAVED` instead
     *
     * @deprecated 2018-11-20
     */
    documentSaved() {
        console.warn(`api.events.documentSaved() is deprecated, use api.events.trigger()`)
        this.trigger(null, Event.DOCUMENT_SAVED)
    }

    /**
     * Use `api.events.trigger()` with `Event.DOCUMENT_SAVE_FAILED` instead
     *
     * @deprecated 2018-11-20
     */
    documentSaveFailed(error) {
        console.warn(`api.events.documentSaveFailed() is deprecated, use api.events.trigger()`)
        this.trigger(null, Event.DOCUMENT_SAVE_FAILED, error)
    }

    /**
     * Use `api.events.trigger()` with `Event.USERACTION_SAVE` instead
     *
     * @deprecated 2018-11-20
     */
    userActionSave() {
        console.warn(`api.events.userActionSave() is deprecated, use api.events.trigger()`)
        this.trigger(null, Event.USERACTION_SAVE)
    }

    /**
     * Use `api.events.trigger()` with `Event.USERACTION_CANCEL_SAVE` instead
     *
     * @deprecated 2018-11-20
     */
    userActionCancelSave() {
        console.warn(`api.events.userActionCancelSave() is deprecated, use api.events.trigger()`)
        this.trigger(null, Event.USERACTION_CANCEL_SAVE)
    }

    /**
     * Use `api.events.trigger()` with `Event.USERACTION_LOCK` instead
     *
     * @deprecated 2018-11-20
     */
    userActionLock() {
        console.warn(`api.events.userActionLock() is deprecated, use api.events.trigger()`)
        this.trigger(null, Event.USERACTION_LOCK)
    }

    /**
     * Use `api.events.trigger()` with `Event.USERACTION_UNLOCK` instead
     *
     * @deprecated 2018-11-20
     */
    userActionUnlock() {
        console.warn(`api.events.userActionUnlock() is deprecated, use api.events.trigger()`)
        this.trigger(null, Event.USERACTION_UNLOCK)
    }

    /**
     * Use `api.events.trigger()` with `Event.ARTICLELANGUAGE_CHANGED` instead
     *
     * @deprecated 2018-11-20
     */
    languageChanged() {
        console.warn(`api.events.languageChanged() is deprecated, use api.events.trigger()`)
        this.trigger(null, Event.ARTICLELANGUAGE_CHANGED, {})
    }

    /**
     * Use `api.events.trigger()` with `"document:startsaving"` instead
     *
     * @deprecated 2018-11-20
     */
    onDocumentStartSaving() {
        console.warn(`api.events.onDocumentStartSaving() is deprecated, use api.events.trigger()`)
        this.trigger(null, 'document:startsaving')
    }

}

export {Events}
